import type { ICachedAsset } from '@lucimark/shared/models/asset'
import type { IPlayerRegistrationPayload } from '@lucimark/shared/models/player-registration-payload'
import type { DBSchema, IDBPDatabase } from 'idb'
import { flattenScheduleRootsToRegistrationPlaylists } from '@lucimark/shared/helpers/registrationPlaylists'
import { coerceRegistrationScheduleRoots } from '@lucimark/shared/models/player-registration-payload'
import { openDB } from 'idb'
import prettyBytes from '@/helpers/prettyBytes'

/** Bumped when persisted registration snapshot shape is incompatible with previous app versions. */
export const REGISTRATION_STATUS_SNAPSHOT_SCHEMA_VERSION = 3
const REGISTRATION_STATUS_SNAPSHOT_ID = 'latest' as const

export interface IRegistrationStatusSnapshotRecord {
  id: typeof REGISTRATION_STATUS_SNAPSHOT_ID
  schemaVersion: number
  savedAtMs: number
  payload: IPlayerRegistrationPayload
}

/** Stored playback heartbeat body for offline queue (proof-of-play). */
export interface IStoredHeartbeat {
  id: string
  at: number
  body: Record<string, unknown>
}

export interface LucimarkDB extends DBSchema {
  asset: {
    key: string
    value: ICachedAsset
    indexes: { lastAccess: Date }
  }
  heartbeatQueue: {
    key: string
    value: IStoredHeartbeat
    indexes: { at: number }
  }
  registrationSnapshot: {
    key: string
    value: IRegistrationStatusSnapshotRecord
  }
}

const DB_VERSION = 4
const HEARTBEAT_QUEUE_MAX = 100
const HEARTBEAT_QUEUE_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24h

export async function getDb(): Promise<IDBPDatabase<LucimarkDB>> {
  const db = await openDB<LucimarkDB>('dohnut-player', DB_VERSION, {
    async upgrade(db, oldVersion, newVersion) {
      if (!db.objectStoreNames.contains('asset')) {
        const store = db.createObjectStore('asset', { keyPath: 'urn' })
        store.createIndex('lastAccess', 'lastAccess')
      }
      if (!db.objectStoreNames.contains('heartbeatQueue')) {
        const store = db.createObjectStore('heartbeatQueue', { keyPath: 'id' })
        store.createIndex('at', 'at')
      }
      if (!db.objectStoreNames.contains('registrationSnapshot')) {
        db.createObjectStore('registrationSnapshot', { keyPath: 'id' })
      }
    }
  })
  return db
}

export interface IDBService {
  getCachedAsset: (urn: string) => Promise<ICachedAsset | null>
  saveCachedAsset: (asset: ICachedAsset) => Promise<ICachedAsset>
  deleteCachedAsset: (urn: string) => Promise<void>
  listAllCachedAssets: () => Promise<ICachedAsset[]>
  purgeCachedAssets: () => Promise<void>
  purgeCachedAssetsExcept: (urnsToKeep: string[]) => Promise<void>
  purgeCachedAssetsOlderThan: (maxAgeMs: number, protectedUrns?: ReadonlySet<string>) => Promise<void>
  enqueueHeartbeat: (body: Record<string, unknown>) => Promise<void>
  getHeartbeatQueue: () => Promise<IStoredHeartbeat[]>
  deleteHeartbeats: (ids: string[]) => Promise<void>
  saveRegistrationStatusSnapshot: (payload: IPlayerRegistrationPayload) => Promise<void>
  getRegistrationStatusSnapshot: () => Promise<IPlayerRegistrationPayload | null>
  getRegistrationStatusSnapshotRecord: () => Promise<IRegistrationStatusSnapshotRecord | null>
  getRegistrationStatusSnapshotSavedAtMs: () => Promise<number | null>
  clearRegistrationStatusSnapshot: () => Promise<void>
}

export default class DBService implements IDBService {
  /**
   * Human-readable storage estimate (used for debug overlay or Sentry context).
   * Uses navigator.storage.estimate(); returns pretty-printed strings (e.g. "12.5 MB").
   */
  static async getStorageEstimate() {
    const storageEstimate = await navigator?.storage?.estimate?.() ?? 0
    const { quota = 0, usage = 0 } = storageEstimate
    const available = quota - usage
    const estimate = {
      available: prettyBytes(available),
      used: prettyBytes(usage),
      quota: prettyBytes(quota)
    }
    return estimate
  }

  /**
   * Raw storage estimate in bytes for logic (e.g. storageUsedPercent for the app players view).
   * Uses navigator.storage.estimate(); returns { quota, usage, available } or null if the Storage API is unavailable.
   * @see docs/player-storage.md
   */
  static async getStorageEstimateRaw(): Promise<{ quota: number, usage: number, available: number } | null> {
    const estimate = await navigator?.storage?.estimate?.()
    if (estimate == null) {
      return null
    }
    const quota = Number(estimate.quota) || 0
    const usage = Number(estimate.usage) || 0
    const available = Math.max(0, quota - usage)

    return { quota, usage, available }
  }

  async getCachedAsset(urn: string): Promise<ICachedAsset | null> {
    const db = await getDb()
    const asset = (await db.get('asset', urn)) ?? null
    if (asset !== null) {
      await db.put('asset', { ...asset, lastAccess: new Date() })
    }
    return asset
  }

  async saveCachedAsset(asset: ICachedAsset): Promise<ICachedAsset> {
    const db = await getDb()
    await db.put('asset', asset)
    return asset
  }

  async deleteCachedAsset(urn: string): Promise<void> {
    const db = await getDb()
    await db.delete('asset', urn)
  }

  /** All rows in the asset store (does not touch `lastAccess`; use for maintenance scans). */
  async listAllCachedAssets(): Promise<ICachedAsset[]> {
    const db = await getDb()
    return db.getAll('asset')
  }

  async purgeCachedAssets() {
    const db = await getDb()
    await db.clear('asset')
  }

  async purgeCachedAssetsExcept(urnsToKeep: string[]) {
    const keepSet = new Set(urnsToKeep)
    const db = await getDb()
    const all = await db.getAll('asset')
    await Promise.all(
      all.filter(a => !keepSet.has(a.urn)).map(a => db.delete('asset', a.urn))
    )
  }

  /**
   * Delete cached assets whose lastAccess is older than maxAgeMs.
   * Used to evict stale assets while keeping recently used ones (minimizes re-downloads).
   * `protectedUrns` are never evicted regardless of age — pass the URNs referenced by the
   * current schedule so a long-offline player keeps the media it still needs to play.
   */
  async purgeCachedAssetsOlderThan(maxAgeMs: number, protectedUrns?: ReadonlySet<string>) {
    const cutoff = Date.now() - maxAgeMs
    const db = await getDb()
    const all = await db.getAll('asset')
    await Promise.all(
      all
        .filter(a => !(protectedUrns?.has(a.urn) === true)
          && (a.lastAccess instanceof Date ? a.lastAccess.getTime() : new Date(a.lastAccess).getTime()) < cutoff)
        .map(a => db.delete('asset', a.urn))
    )
  }

  async enqueueHeartbeat(body: Record<string, unknown>) {
    const db = await getDb()
    const id = crypto.randomUUID()
    const at = Date.now()
    await db.put('heartbeatQueue', { id, at, body })
    const all = await db.getAllFromIndex('heartbeatQueue', 'at')
    const cutoff = at - HEARTBEAT_QUEUE_MAX_AGE_MS
    const toKeep = all.filter(x => x.at >= cutoff).slice(-HEARTBEAT_QUEUE_MAX)
    const toDelete = all.filter(x => !toKeep.some(k => k.id === x.id))
    for (const x of toDelete) {
      await db.delete('heartbeatQueue', x.id)
    }
  }

  async getHeartbeatQueue(): Promise<IStoredHeartbeat[]> {
    const db = await getDb()
    const list = await db.getAllFromIndex('heartbeatQueue', 'at')
    const cutoff = Date.now() - HEARTBEAT_QUEUE_MAX_AGE_MS
    return list.filter(x => x.at >= cutoff)
  }

  async deleteHeartbeats(ids: string[]) {
    const db = await getDb()
    for (const id of ids) {
      await db.delete('heartbeatQueue', id)
    }
  }

  async saveRegistrationStatusSnapshot(payload: IPlayerRegistrationPayload) {
    const db = await getDb()
    const row: IRegistrationStatusSnapshotRecord = {
      id: REGISTRATION_STATUS_SNAPSHOT_ID,
      schemaVersion: REGISTRATION_STATUS_SNAPSHOT_SCHEMA_VERSION,
      savedAtMs: Date.now(),
      payload
    }
    await db.put('registrationSnapshot', row)
  }

  /** Full schema-compatible snapshot row (payload + savedAtMs). Null when absent or incompatible. */
  async getRegistrationStatusSnapshotRecord(): Promise<IRegistrationStatusSnapshotRecord | null> {
    const db = await getDb()
    const row = (await db.get('registrationSnapshot', REGISTRATION_STATUS_SNAPSHOT_ID)) ?? null
    if (row === null || row.schemaVersion !== REGISTRATION_STATUS_SNAPSHOT_SCHEMA_VERSION) {
      return null
    }
    const payload = row.payload
    if (payload.playlists != null) {
      return row
    }
    if (payload.schedule != null && payload.schedule.length > 0) {
      return {
        ...row,
        payload: {
          ...payload,
          playlists: flattenScheduleRootsToRegistrationPlaylists(
            coerceRegistrationScheduleRoots(payload.schedule)
          ),
          schedule: undefined
        }
      }
    }
    return row
  }

  async getRegistrationStatusSnapshot(): Promise<IPlayerRegistrationPayload | null> {
    const row = await this.getRegistrationStatusSnapshotRecord()
    return row?.payload ?? null
  }

  /** When the current (schema-compatible) snapshot was saved; null when absent or incompatible. Diagnostics only. */
  async getRegistrationStatusSnapshotSavedAtMs(): Promise<number | null> {
    const row = await this.getRegistrationStatusSnapshotRecord()
    return row?.savedAtMs ?? null
  }

  async clearRegistrationStatusSnapshot() {
    const db = await getDb()
    await db.delete('registrationSnapshot', REGISTRATION_STATUS_SNAPSHOT_ID)
  }
}

export type TDBService = DBService
